import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRightIcon, LinkIcon, PlusIcon, MessageSquareIcon, FileIcon, ExternalLinkIcon } from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { ChecklistItem, LinkRef } from '../../types';
import { useWorkspace } from '../../store/workspaceStore';
import { StatusSelect } from '../common/StatusPill';
import { Avatar } from '../common/Avatar';
const KIND_LABEL: Record<LinkRef['kind'], string> = {
  powerpoint: 'PowerPoint',
  dropbox: 'Dropbox',
  sheet: 'Google Sheet',
  sharepoint: 'SharePoint',
  other: 'Link'
};
export function ChecklistRow({
  proposalId,
  item
}: {
  proposalId: string;
  item: ChecklistItem;
}) {
  const [expanded, setExpanded] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [linkLabel, setLinkLabel] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkKind, setLinkKind] = useState<LinkRef['kind']>('dropbox');
  const setItemStatus = useWorkspace(s => s.setItemStatus);
  const addItemLink = useWorkspace(s => s.addItemLink);
  const addItemNote = useWorkspace(s => s.addItemNote);
  const personById = useWorkspace(s => s.personById);
  const updater = personById(item.updatedById);
  const detailCount = item.links.length + item.notes.length;
  const submitLink = () => {
    if (!linkLabel.trim() || !linkUrl.trim()) return;
    addItemLink(proposalId, item.key, {
      label: linkLabel.trim(),
      url: linkUrl.trim(),
      kind: linkKind
    });
    setLinkLabel('');
    setLinkUrl('');
    setLinkOpen(false);
    setExpanded(true);
  };
  const submitNote = () => {
    if (!noteText.trim()) return;
    addItemNote(proposalId, item.key, noteText.trim());
    setNoteText('');
    setExpanded(true);
  };
  return <div className="rounded-xl border border-slate-200 bg-white" data-id="element-66" data-mp="97vfw2xv0130">
      <div className="flex items-center gap-3 px-4 py-3" data-id="element-67" data-mp="973x5spd1xu5">
        <button onClick={() => setExpanded(e => !e)} className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-600" aria-label={expanded ? 'Collapse' : 'Expand'} data-id="element-68" data-mp="6tgvp9khwybh">
          <ChevronRightIcon className={`h-4 w-4 transition-transform ${expanded ? 'rotate-90' : ''}`} data-id="element-69" data-mp="3tzr9qwoccfk5" />
        </button>

        <div className="min-w-0 flex-1" data-id="element-70" data-mp="qhplxe2hau8c">
          <div className="flex items-center gap-2" data-id="element-71" data-mp="qgoc1cx78ds7">
            <p className="truncate text-sm font-semibold text-slate-800" data-id="element-72" data-mp="licxiovylf3n">{item.label}</p>
            {detailCount > 0 && <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500" data-id="element-73" data-mp="gqcvmqwbwc4f">{detailCount}</span>}
          </div>
          {item.hint && <p className="truncate text-xs text-slate-400" data-id="element-74" data-mp="1987cbbzu9dqj">{item.hint}</p>}
        </div>

        {updater && <div className="hidden items-center gap-2 text-right sm:flex" data-id="element-75" data-mp="14gpa6km2x73z">
            <div className="leading-tight" data-id="element-76" data-mp="1bs54jv42g4xh">
              <p className="text-[11px] font-medium text-slate-600" data-id="element-77" data-mp="1btf2mkax5d44">{updater.name}</p>
              <p className="text-[10px] text-slate-400" data-id="element-78" data-mp="1gs9k9ix5gvmn">{item.updatedAt ? format(new Date(item.updatedAt), 'MMM d, h:mma') : ''}</p>
            </div>
            <Avatar person={updater} size={26} data-id="element-79" data-mp="1lsnu96ibafnb" />
          </div>}

        <StatusSelect status={item.status} onChange={s => setItemStatus(proposalId, item.key, s)} data-id="element-80" data-mp="1qqz25bp1qg2w" />
      </div>

      <AnimatePresence initial={false} data-id="element-81" data-mp="1uowtol6ahe9g">
        {expanded && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.18
      }} className="overflow-hidden" data-id="element-82" data-mp="1zp11lmt4t0o2">
            <div className="space-y-4 border-t border-slate-100 px-4 py-4" data-id="element-83" data-mp="2ef8jo1f88ho8">
              {/* Links */}
              <section data-id="element-84" data-mp="2igajokgywxr9">
                <div className="mb-2 flex items-center justify-between" data-id="element-85" data-mp="2ihlnulebs7iw">
                  <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-86" data-mp="3g4x1zne8uas5">
                    <LinkIcon className="h-3.5 w-3.5" data-id="element-87" data-mp="3b5aqbie2d2lg" /> Files & Links
                  </h4>
                  <button onClick={() => setLinkOpen(o => !o)} className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-brand-accent hover:bg-blue-50" data-id="element-88" data-mp="3oy0cpnxi89j3">
                    <PlusIcon className="h-3.5 w-3.5" data-id="element-89" data-mp="2x8otmapxpjsm" /> Add link
                  </button>
                </div>

                {item.links.length === 0 && !linkOpen && <p className="text-xs text-slate-400" data-id="element-90" data-mp="36827rgp06wjo">No files attached yet.</p>}

                <div className="space-y-1.5" data-id="element-91" data-mp="317ec2hptxrj4">
                  {item.links.map(l => {
                const by = personById(l.addedById);
                return <a key={l.id} href={l.url} target="_blank" rel="noreferrer" className="group flex items-center gap-2.5 rounded-lg border border-slate-200 px-3 py-2 hover:border-brand-accent hover:bg-blue-50/40" data-id="element-92" data-mp="giqggwgapyj8">
                        <FileIcon className="h-4 w-4 text-slate-400" data-id="element-93" data-mp="jzuwzt66h4mp" />
                        <div className="min-w-0 flex-1" data-id="element-94" data-mp="k04m7ejef7rc">
                          <p className="truncate text-sm font-medium text-slate-700" data-id="element-95" data-mp="17p71ifqk8oio">{l.label}</p>
                          <p className="text-[11px] text-slate-400" data-id="element-96" data-mp="1cotf4qaaw6s0">
                            {KIND_LABEL[l.kind]} · {by?.name} · {format(new Date(l.addedAt), 'MMM d')}
                          </p>
                        </div>
                        <ExternalLinkIcon className="h-4 w-4 text-slate-300 group-hover:text-brand-accent" data-id="element-97" data-mp="12x5w9drt7y25" />
                      </a>;
              })}
                </div>

                <AnimatePresence data-id="element-98" data-mp="1vgma2q8xk22d">
                  {linkOpen && <motion.div initial={{
                opacity: 0,
                y: -4
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -4
              }} className="mt-2 space-y-2 rounded-lg bg-slate-50 p-3" data-id="element-99" data-mp="25b9yh9hr2ukb">
                      <div className="flex gap-2" data-id="element-100" data-mp="1rbs4mfplkr43">
                        <input value={linkLabel} onChange={e => setLinkLabel(e.target.value)} placeholder="Label (e.g. Harborview OM.pdf)" className="flex-1 rounded-md border border-slate-200 px-2.5 py-1.5 text-sm outline-none focus:border-brand-accent" data-id="element-101" data-mp="1raz96aw39o19" />
                        <select value={linkKind} onChange={e => setLinkKind(e.target.value as LinkRef['kind'])} className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm outline-none" data-id="element-102" data-mp="17ouh07pbpwwr">
                          <option value="dropbox" data-id="element-103" data-mp="3a9nd719c02i9">Dropbox</option>
                          <option value="powerpoint" data-id="element-104" data-mp="3f88b2u142a3j">PowerPoint</option>
                          <option value="sheet" data-id="element-105" data-mp="3jauymiqr18ux">Google Sheet</option>
                          <option value="sharepoint" data-id="element-106" data-mp="3j8ihu8wq94jg">SharePoint</option>
                          <option value="other" data-id="element-107" data-mp="3o8epci4mve3r">Other</option>
                        </select>
                      </div>
                      <input value={linkUrl} onChange={e => setLinkUrl(e.target.value)} placeholder="https://…" className="w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-sm outline-none focus:border-brand-accent" data-id="element-108" data-mp="2rl9za9nxrqby" />
                      <div className="flex justify-end gap-2" data-id="element-109" data-mp="27vzmb3fkrjhj">
                        <button onClick={() => setLinkOpen(false)} className="rounded-md px-3 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-100" data-id="element-110" data-mp="27tlr8ukwa79z">
                          Cancel
                        </button>
                        <button onClick={submitLink} className="rounded-md bg-brand-accent px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand" data-id="element-111" data-mp="118dp73qxbtgv">
                          Attach
                        </button>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </section>

              {/* Notes */}
              <section data-id="element-112" data-mp="1p3q8pclpws9d">
                <h4 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-113" data-mp="1k7hxgaan5j7h">
                  <MessageSquareIcon className="h-3.5 w-3.5" data-id="element-114" data-mp="1yukt0bfelhs6" /> Notes
                </h4>
                <div className="space-y-2" data-id="element-115" data-mp="1txucjmzosk68">
                  {item.notes.map(n => {
                const author = personById(n.authorId);
                return <div key={n.id} className="flex gap-2.5" data-id="element-116" data-mp="22xzl40gm9olz">
                        <Avatar person={author} size={26} data-id="element-117" data-mp="22vejnck8daor" />
                        <div className="flex-1 rounded-lg bg-slate-50 px-3 py-2" data-id="element-118" data-mp="2mkkntzbqrp6v">
                          <p className="text-sm text-slate-700" data-id="element-119" data-mp="2rim7omgzrsje">{n.text}</p>
                          <p className="mt-0.5 text-[11px] text-slate-400" data-id="element-120" data-mp="2diuwsulrus9m">
                            {author?.name} · {formatDistanceToNow(new Date(n.createdAt), {
                        addSuffix: true
                      })}
                          </p>
                        </div>
                      </div>;
              })}
                </div>
                <div className="mt-2 flex gap-2" data-id="element-121" data-mp="28kjkmd8085o8">
                  <input value={noteText} onChange={e => setNoteText(e.target.value)} onKeyDown={e => e.key === 'Enter' && submitNote()} placeholder="Add a note for the team…" className="flex-1 rounded-md border border-slate-200 px-2.5 py-1.5 text-sm outline-none focus:border-brand-accent" data-id="element-122" data-mp="28lb4wrw5igz7" />
                  <button onClick={submitNote} className="rounded-md bg-slate-800 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-900" data-id="element-123" data-mp="lo6vfp8z3nn4">
                    Post
                  </button>
                </div>
              </section>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}